/**
 * NIGHT OWL - Knowledge Graph Visualization
 *
 * Displays extracted knowledge as Mermaid diagrams
 * Auto-scrolling virtual list of knowledge graphs
 */

import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

export interface KnowledgeEntry {
  id: string;
  timestamp: string;
  domain: string;
  entities: Array<{
    name: string;
    type: string;
    description: string;
  }>;
  relations: Array<{
    subject: string;
    predicate: string;
    object: string;
    confidence: number;
  }>;
  facts: Array<{
    statement: string;
    confidence: number;
    source: string;
  }>;
}

interface KnowledgeGraphProps {
  entry: KnowledgeEntry;
}

function KnowledgeGraphCard({ entry }: KnowledgeGraphProps) {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (!graphRef.current || isRendered) return;

    // Generate Mermaid diagram from knowledge
    const diagram = generateMermaidDiagram(entry);

    // Render Mermaid diagram
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      themeVariables: {
        primaryColor: '#8b5cf6',
        primaryTextColor: '#fff',
        primaryBorderColor: '#6d28d9',
        lineColor: '#a78bfa',
        secondaryColor: '#3b82f6',
        tertiaryColor: '#10b981'
      },
      flowchart: {
        curve: 'basis',
        padding: 20
      }
    });

    const renderDiagram = async () => {
      try {
        const { svg } = await mermaid.render(
          `graph-${entry.id}`,
          diagram
        );
        if (graphRef.current) {
          graphRef.current.innerHTML = svg;
          setIsRendered(true);
        }
      } catch (error) {
        console.error('Mermaid rendering error:', error);
        if (graphRef.current) {
          graphRef.current.innerHTML = `<div class="text-red-400 text-xs">Rendering error</div>`;
        }
      }
    };

    renderDiagram();
  }, [entry, isRendered]);

  return (
    <div className="glass-panel p-4 mb-4 border border-purple-800/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
          <span className="text-sm font-mono text-purple-400">
            Knowledge Domain: {entry.domain}
          </span>
        </div>
        <div className="text-[10px] text-gray-600 font-mono">
          {new Date(entry.timestamp).toLocaleTimeString()}
        </div>
      </div>

      {/* Mermaid Graph */}
      <div
        ref={graphRef}
        className="mermaid-container overflow-x-auto min-h-[300px] flex items-center justify-center"
        style={{
          maxHeight: '600px'
        }}
      />

      {/* Statistics */}
      <div className="mt-3 pt-3 border-t border-gray-800 grid grid-cols-3 gap-2 text-[10px]">
        <div className="text-center">
          <div className="text-cyan-400 font-bold text-lg">{entry.entities.length}</div>
          <div className="text-gray-500">Entities</div>
        </div>
        <div className="text-center">
          <div className="text-purple-400 font-bold text-lg">{entry.relations.length}</div>
          <div className="text-gray-500">Relations</div>
        </div>
        <div className="text-center">
          <div className="text-green-400 font-bold text-lg">{entry.facts.length}</div>
          <div className="text-gray-500">Facts</div>
        </div>
      </div>
    </div>
  );
}

function generateMermaidDiagram(entry: KnowledgeEntry): string {
  const lines: string[] = ['graph TD'];

  // Add entity nodes (limit to top 15 most connected)
  const entityMap = new Map<string, string>();
  let nodeId = 0;

  entry.entities.slice(0, 15).forEach(entity => {
    const id = `E${nodeId++}`;
    entityMap.set(entity.name, id);

    // Style based on entity type
    let style = '';
    switch (entity.type) {
      case 'CONCEPT':
        style = ':::concept';
        break;
      case 'PERSON':
        style = ':::person';
        break;
      case 'ORGANIZATION':
        style = ':::org';
        break;
      case 'THEORY':
        style = ':::theory';
        break;
      default:
        style = ':::default';
    }

    lines.push(`  ${id}["${sanitizeMermaid(entity.name)}"]${style}`);
  });

  // Add relations (limit to top 20 by confidence)
  const topRelations = entry.relations
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 20);

  topRelations.forEach(relation => {
    const subjectId = entityMap.get(relation.subject);
    const objectId = entityMap.get(relation.object);

    if (subjectId && objectId) {
      const label = sanitizeMermaid(relation.predicate);
      lines.push(`  ${subjectId} -->|"${label}"| ${objectId}`);
    }
  });

  // Add styling classes
  lines.push('');
  lines.push('  classDef concept fill:#8b5cf6,stroke:#6d28d9,stroke-width:2px,color:#fff');
  lines.push('  classDef person fill:#3b82f6,stroke:#2563eb,stroke-width:2px,color:#fff');
  lines.push('  classDef org fill:#10b981,stroke:#059669,stroke-width:2px,color:#fff');
  lines.push('  classDef theory fill:#f59e0b,stroke:#d97706,stroke-width:2px,color:#fff');
  lines.push('  classDef default fill:#6b7280,stroke:#4b5563,stroke-width:2px,color:#fff');

  return lines.join('\n');
}

function sanitizeMermaid(text: string): string {
  // Escape special characters for Mermaid
  return text
    .replace(/"/g, "'")
    .replace(/\[/g, '(')
    .replace(/\]/g, ')')
    .replace(/\n/g, ' ')
    .slice(0, 40); // Limit length for readability
}

export default function KnowledgeGraph() {
  const [knowledgeEntries, setKnowledgeEntries] = useState<KnowledgeEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [stats, setStats] = useState({ total: 0, entities: 0, relations: 0, facts: 0 });
  const wsRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Connect to WebSocket
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/knowledge`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected to knowledge extraction');
      setIsConnected(true);
      // Auto-start extraction on page load
      setTimeout(() => {
        requestNextKnowledge();
      }, 1000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    };

    return () => {
      ws.close();
    };
  }, []);

  // Auto-scroll to latest knowledge graph
  useEffect(() => {
    if (containerRef.current && knowledgeEntries.length > 0) {
      const container = containerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [knowledgeEntries]);

  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case 'knowledge_extracted':
        const newEntry: KnowledgeEntry = {
          id: `kg-${Date.now()}-${Math.random().toString().replace('.', '')}`,
          timestamp: data.timestamp,
          domain: data.knowledge.article,
          entities: data.knowledge.entities,
          relations: data.knowledge.relations,
          facts: data.knowledge.facts
        };

        setKnowledgeEntries(prev => {
          const updated = [...prev, newEntry];
          // Keep last 10 entries to prevent memory issues
          return updated.slice(-10);
        });

        // Update stats
        setStats(prev => ({
          total: prev.total + 1,
          entities: prev.entities + data.knowledge.entities.length,
          relations: prev.relations + data.knowledge.relations.length,
          facts: prev.facts + data.knowledge.facts.length
        }));

        // Immediately request next knowledge
        isProcessingRef.current = false;
        setTimeout(() => requestNextKnowledge(), 800);
        break;

      case 'extraction_started':
        isProcessingRef.current = true;
        break;

      case 'extraction_error':
        isProcessingRef.current = false;
        setTimeout(() => requestNextKnowledge(), 2000);
        break;
    }
  };

  const requestNextKnowledge = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isProcessingRef.current) {
      wsRef.current.send(JSON.stringify({ action: 'extract_next' }));
      isProcessingRef.current = true;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-950">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2 bg-gray-900 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-xs font-mono text-gray-400">
              {isConnected ? 'ACTIVE' : 'DISCONNECTED'}
            </span>
          </div>
        </div>
        <div className="text-xs font-mono text-gray-500">
          {stats.total} knowledge domains processed
        </div>
      </div>

      {/* Knowledge Graphs - Auto-scrolling */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ scrollbarWidth: 'thin' }}
      >
        {knowledgeEntries.length === 0 && (
          <div className="text-center text-gray-600 mt-8">
            <div className="text-4xl mb-2">🧠</div>
            <div>Extracting symbolic knowledge...</div>
            <div className="text-[10px] mt-2">Neurosymbolic AI processing</div>
          </div>
        )}

        {knowledgeEntries.map((entry) => (
          <KnowledgeGraphCard key={entry.id} entry={entry} />
        ))}
      </div>

      {/* Statistics Panel */}
      {stats.total > 0 && (
        <div className="flex-shrink-0 border-t border-gray-800 p-3 bg-gray-900">
          <div className="text-xs font-mono mb-2 text-gray-400">CUMULATIVE KNOWLEDGE</div>
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <div className="glass-panel p-2">
              <div className="text-gray-500">Total Entities</div>
              <div className="text-lg font-bold text-cyan-400">{stats.entities}</div>
            </div>
            <div className="glass-panel p-2">
              <div className="text-gray-500">Total Relations</div>
              <div className="text-lg font-bold text-purple-400">{stats.relations}</div>
            </div>
            <div className="glass-panel p-2">
              <div className="text-gray-500">Total Facts</div>
              <div className="text-lg font-bold text-green-400">{stats.facts}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
